import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import './DjDeck.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

type DeckKey = 'A' | 'B';

type RadiojarConfig = {
  host: string;
  port: string;
  encoding: string;
  mountPoint: string;
  username: string;
  password: string;
};

type RadiojarResponse = {
  success?: boolean;
  connected?: boolean;
  statusCode?: number;
  message?: string;
  error?: string;
  details?: string;
  transportError?: string;
  keepAlive?: boolean;
  keepAliveWarning?: string | null;
  keepAliveDetails?: string | null;
  keepAliveStartedAt?: string | null;
  config?: Partial<RadiojarConfig>;
};

type DjTrack = {
  id: string;
  title: string;
  artist: string;
  audioUrl: string;
  imageUrl?: string;
  sourceFolderId?: string;
  source: 'playlist' | 'upload' | 'server' | 'saved';
};

type DjMusicFolder = {
  id: string;
  label: string;
  path: string;
  createdAt?: string;
  readOnly?: boolean;
};

type SavedPlaylistMeta = {
  id: number;
  name: string;
  songCount: number;
  songsPerRound: number;
  created_at?: string;
  updated_at?: string;
};

type SavedPlaylist = SavedPlaylistMeta & {
  songs: Array<{
    id: string;
    title: string;
    artist?: string;
    audioUrl?: string;
    previewUrl?: string;
    spotifyId?: string;
    spotifyUri?: string;
    youtubeVideoId?: string;
    imageUrl?: string;
    source?: DjTrack['source'];
  }>;
};

type DashboardPlaylistOption = {
  id: string;
  name: string;
  songCount: number;
  songs: SavedPlaylist['songs'];
};

type DeckState = {
  queue: DjTrack[];
  loadedIndex: number | null;
  isPlaying: boolean;
  status: string;
  currentTime: number;
  duration: number;
  volume: number;
};

const DEFAULT_RADIOJAR_CONFIG: RadiojarConfig = {
  host: 'link.radiojar.com',
  port: '80',
  encoding: 'MP3',
  mountPoint: 'mkw8r48hxgpwv/master',
  username: 'source',
  password: 'SxTl9Ylp7F',
};

const initialDeckState = (): DeckState => ({
  queue: [],
  loadedIndex: null,
  isPlaying: false,
  status: 'Stil',
  currentTime: 0,
  duration: 0,
  volume: 0.9,
});

const fmtTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const readResponse = async (response: Response): Promise<RadiojarResponse> => {
  const raw = await response.text();
  if (!raw.trim()) return {};
  try {
    return JSON.parse(raw) as RadiojarResponse;
  } catch {
    return { details: raw };
  }
};

const isBlobUrl = (url: string): boolean => url.startsWith('blob:');

const hasStableAudioUrl = (url: string): boolean => {
  const trimmed = String(url || '').trim();
  if (!trimmed) return false;
  return !isBlobUrl(trimmed);
};

const buildEventSongEntry = (song: any, fallbackIdPrefix: string, index: number): SavedPlaylist['songs'][number] => ({
  id: String(song?.id || `${fallbackIdPrefix}-${index}`),
  title: String(song?.title || 'Onbekend nummer'),
  artist: String(song?.artist || 'Onbekende artiest'),
  audioUrl: String(song?.audioUrl || song?.previewUrl || song?.streamUrl || song?.url || ''),
  previewUrl: String(song?.previewUrl || ''),
  spotifyId: song?.spotifyId ? String(song.spotifyId) : undefined,
  spotifyUri: song?.spotifyUri ? String(song.spotifyUri) : undefined,
  youtubeVideoId: song?.youtubeVideoId ? String(song.youtubeVideoId) : undefined,
  imageUrl: String(song?.imageUrl || ''),
  source: 'playlist' as const,
});

const buildAutoPlaylistName = (): string => {
  const now = new Date();
  const datePart = now.toLocaleDateString('nl-NL').replace(/\//g, '-');
  const timePart = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }).replace(':', '.');
  return `DJ Playlist ${datePart} ${timePart}`;
};

const DjDeck: React.FC = () => {
  const navigate = useNavigate();
  const { playlist } = useGame();

  const [clock, setClock] = useState('');
  const [library, setLibrary] = useState<DjTrack[]>([]);
  const [selectedLibraryTrackId, setSelectedLibraryTrackId] = useState<string | null>(null);
  const [deckA, setDeckA] = useState<DeckState>(initialDeckState);
  const [deckB, setDeckB] = useState<DeckState>(initialDeckState);
  const [crossfader, setCrossfader] = useState(0.5);
  const [masterVolume, setMasterVolume] = useState(1);
  const [vuLevel, setVuLevel] = useState(0);
  const [autoDjEnabled, setAutoDjEnabled] = useState(false);
  const [activeDeck, setActiveDeck] = useState<DeckKey>('A');
  const [radiojarConfig, setRadiojarConfig] = useState<RadiojarConfig>(DEFAULT_RADIOJAR_CONFIG);
  const [radiojarLoading, setRadiojarLoading] = useState(false);
  const [radiojarConnected, setRadiojarConnected] = useState(false);
  const [radiojarStatus, setRadiojarStatus] = useState('Offline');
  const [radiojarError, setRadiojarError] = useState<string | null>(null);
  const [showRadiojarSettings, setShowRadiojarSettings] = useState(false);
  const [musicFolders, setMusicFolders] = useState<DjMusicFolder[]>([]);
  const [selectedMusicFolderId, setSelectedMusicFolderId] = useState<string | null>(null);
  const [libraryStatus, setLibraryStatus] = useState<string | null>(null);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [savedPlaylists, setSavedPlaylists] = useState<SavedPlaylistMeta[]>([]);
  const [selectedSavedPlaylistId, setSelectedSavedPlaylistId] = useState<number | null>(null);
  const [dashboardPlaylists, setDashboardPlaylists] = useState<DashboardPlaylistOption[]>([]);
  const [selectedDashboardPlaylistId, setSelectedDashboardPlaylistId] = useState('');
  const [savedPlaylistName, setSavedPlaylistName] = useState('');
  const [playlistBusy, setPlaylistBusy] = useState(false);
  const [playlistStatus, setPlaylistStatus] = useState<string | null>(null);
  const [selectedPlaylistTracks, setSelectedPlaylistTracks] = useState<DjTrack[]>([]);
  const [playlistTrackSearch, setPlaylistTrackSearch] = useState('');
  const [playlistTrackSourceFilter, setPlaylistTrackSourceFilter] = useState<'all' | DjTrack['source']>('all');

  const deckARef = useRef<HTMLAudioElement | null>(null);
  const deckBRef = useRef<HTMLAudioElement | null>(null);
  const uploadARef = useRef<HTMLInputElement | null>(null);
  const uploadBRef = useRef<HTMLInputElement | null>(null);
  const uploadLibraryRef = useRef<HTMLInputElement | null>(null);
  const deckAStateRef = useRef(deckA);
  const deckBStateRef = useRef(deckB);
  const autoMixingRef = useRef(false);
  const broadcastAbortRef = useRef<AbortController | null>(null);
  const broadcastControllerRef = useRef<ReadableStreamDefaultController<Uint8Array> | null>(null);
  const broadcastRecorderRef = useRef<MediaRecorder | null>(null);
  const broadcastAudioContextRef = useRef<AudioContext | null>(null);
  const broadcastSourceNodesRef = useRef<AudioNode[]>([]);

  const playlistLibraryTracks = useMemo<DjTrack[]>(() => {
    return playlist
      .filter((song) => typeof song.audioUrl === 'string' && song.audioUrl.trim().length > 0)
      .map((song) => ({
        id: `playlist-${song.id}`,
        title: song.title,
        artist: song.artist,
        audioUrl: song.audioUrl,
        imageUrl: song.imageUrl,
        source: 'playlist' as const,
      }));
  }, [playlist]);

  useEffect(() => {
    setLibrary((current) => {
      const nonPlaylistTracks = current.filter((track) => track.source !== 'playlist');
      const merged = [...playlistLibraryTracks, ...nonPlaylistTracks];
      const unique = new Map<string, DjTrack>();
      merged.forEach((track) => {
        unique.set(track.id, track);
      });
      return Array.from(unique.values());
    });
  }, [playlistLibraryTracks]);

  useEffect(() => {
    deckAStateRef.current = deckA;
  }, [deckA]);

  useEffect(() => {
    deckBStateRef.current = deckB;
  }, [deckB]);

  const stopBrowserBroadcast = useCallback(async () => {
    const recorder = broadcastRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        // ignore stop errors
      }
    }

    broadcastRecorderRef.current = null;

    if (broadcastAbortRef.current) {
      broadcastAbortRef.current.abort();
      broadcastAbortRef.current = null;
    }

    if (broadcastControllerRef.current) {
      try {
        broadcastControllerRef.current.close();
      } catch {
        // ignore close errors
      }
      broadcastControllerRef.current = null;
    }

    broadcastSourceNodesRef.current.forEach((node) => {
      try {
        node.disconnect();
      } catch {
        // ignore disconnect errors
      }
    });
    broadcastSourceNodesRef.current = [];

    if (broadcastAudioContextRef.current) {
      try {
        await broadcastAudioContextRef.current.close();
      } catch {
        // ignore close errors
      }
      broadcastAudioContextRef.current = null;
    }
  }, []);

  const startBrowserBroadcast = useCallback(async () => {
    await stopBrowserBroadcast();

    const audioA = deckARef.current;
    const audioB = deckBRef.current;
    if (!audioA || !audioB) {
      throw new Error('Deck audio elementen zijn nog niet beschikbaar');
    }

    const audioContext = new AudioContext();
    await audioContext.resume();
    const destination = audioContext.createMediaStreamDestination();
    const sourceA = audioContext.createMediaElementSource(audioA);
    const sourceB = audioContext.createMediaElementSource(audioB);
    sourceA.connect(destination);
    sourceB.connect(destination);

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';

    const streamController = new ReadableStream<Uint8Array>({
      start(controller) {
        broadcastControllerRef.current = controller;
      },
      cancel() {
        broadcastControllerRef.current = null;
      },
    });

    const abortController = new AbortController();
    broadcastAbortRef.current = abortController;
    broadcastAudioContextRef.current = audioContext;
    broadcastSourceNodesRef.current = [sourceA, sourceB];

    const broadcastRequestInit = {
      method: 'POST',
      headers: { 'Content-Type': mimeType },
      body: streamController as any,
      signal: abortController.signal,
      duplex: 'half' as const,
    } as RequestInit & { duplex: 'half' };

    const uploadPromise = fetch(`${API_BASE}/api/radiojar/broadcast`, broadcastRequestInit).catch((error) => {
      throw error;
    });

    const recorder = new MediaRecorder(destination.stream, { mimeType });
    broadcastRecorderRef.current = recorder;

    recorder.ondataavailable = async (event) => {
      if (!event.data || event.data.size === 0 || !broadcastControllerRef.current) {
        return;
      }

      try {
        const buffer = new Uint8Array(await event.data.arrayBuffer());
        broadcastControllerRef.current.enqueue(buffer);
      } catch (error) {
        console.warn('Could not enqueue broadcast chunk:', error);
      }
    };

    recorder.onerror = () => {
      setRadiojarError('Browser broadcast kon niet starten');
    };

    recorder.onstop = () => {
      if (broadcastControllerRef.current) {
        try {
          broadcastControllerRef.current.close();
        } catch {
          // ignore
        }
        broadcastControllerRef.current = null;
      }
    };

    recorder.start(1000);
    setRadiojarStatus('Live verbonden en browser-audio wordt uitgezonden');
    setRadiojarError(null);

    void uploadPromise;
  }, [stopBrowserBroadcast]);

  useEffect(() => {
    const tick = () => {
      setClock(new Date().toLocaleTimeString('nl-NL'));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const loadSavedPlaylists = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/saved-playlists`);
      if (!response.ok) return;
      const data = await response.json();
      setSavedPlaylists(Array.isArray(data) ? data : []);
    } catch {
      setSavedPlaylists([]);
    }
  }, []);

  const loadDashboardPlaylists = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/events`);
      if (!response.ok) {
        setDashboardPlaylists([]);
        return;
      }

      const events = await response.json();
      const eventList = Array.isArray(events) ? events : [];
      const nextPlaylists: DashboardPlaylistOption[] = [];

      eventList.forEach((event: any) => {
        const eventId = String(event?.id || '');
        const eventName = String(event?.name || 'Event');
        const allEventSongs: SavedPlaylist['songs'] = [];

        const rounds = Array.isArray(event?.rounds) ? event.rounds : [];
        rounds.forEach((round: any) => {
          const roundId = String(round?.id || '');
          const roundName = String(round?.name || 'Ronde');
          const roundSongs = Array.isArray(round?.songs) ? round.songs : [];
          if (roundSongs.length === 0) return;

          const mappedRoundSongs = roundSongs.map((song: any, index: number) =>
            buildEventSongEntry(song, `event-${eventId}-round-${roundId || 'x'}-song`, index)
          );
          allEventSongs.push(...mappedRoundSongs);

          nextPlaylists.push({
            id: `event:${eventId}:round:${roundId}`,
            name: `${eventName} / ${roundName}`,
            songCount: mappedRoundSongs.length,
            songs: mappedRoundSongs,
          });
        });

        const themeSongs = Array.isArray(event?.themeSongs) ? event.themeSongs : [];
        if (themeSongs.length > 0) {
          const mappedThemeSongs = themeSongs.map((song: any, index: number) =>
            buildEventSongEntry(song, `event-${eventId}-theme-song`, index)
          );
          allEventSongs.push(...mappedThemeSongs);

          nextPlaylists.push({
            id: `event:${eventId}:theme`,
            name: `${eventName} / Thema-avond`,
            songCount: mappedThemeSongs.length,
            songs: mappedThemeSongs,
          });
        }

        const legacyEventSongs = Array.isArray(event?.songs) ? event.songs : [];
        if (legacyEventSongs.length > 0) {
          const mappedLegacySongs = legacyEventSongs.map((song: any, index: number) =>
            buildEventSongEntry(song, `event-${eventId}-legacy-song`, index)
          );
          allEventSongs.push(...mappedLegacySongs);
        }

        if (allEventSongs.length > 0) {
          nextPlaylists.unshift({
            id: `event:${eventId}:all`,
            name: `${eventName} / Alles`,
            songCount: allEventSongs.length,
            songs: allEventSongs,
          });
        }
      });

      setDashboardPlaylists(nextPlaylists);
    } catch {
      setDashboardPlaylists([]);
    }
  }, []);

  const mapSavedSongsToTracks = useCallback((songs: SavedPlaylist['songs'] = []) => {
    const mappedTracks: DjTrack[] = songs
      .map((song) => {
        const rawId = String(song.id || `saved-${Math.random().toString(36).slice(2, 7)}`);
        let inferredSource: DjTrack['source'] = 'saved';
        if (song.source && ['playlist', 'upload', 'server', 'saved'].includes(song.source)) {
          inferredSource = song.source;
        } else if (rawId.startsWith('playlist-')) {
          inferredSource = 'playlist';
        } else if (rawId.startsWith('server-')) {
          inferredSource = 'server';
        } else if (rawId.startsWith('upload-') || rawId.startsWith('library-upload-') || rawId.startsWith('local-folder-')) {
          inferredSource = 'upload';
        }

        return {
          id: `saved-preview-${rawId}-${Math.random().toString(36).slice(2, 7)}`,
          title: song.title || 'Onbekend nummer',
          artist: song.artist || 'Onbekende artiest',
          audioUrl: String(song.audioUrl || song.previewUrl || ''),
          imageUrl: song.imageUrl,
          source: inferredSource,
        };
      });

    return {
      tracks: mappedTracks,
      skippedCount: mappedTracks.filter((track) => !hasStableAudioUrl(track.audioUrl)).length,
    };
  }, []);

  const loadSelectedPlaylistTracks = useCallback(async (playlistId: number) => {
    try {
      const response = await fetch(`${API_BASE}/api/saved-playlists/${playlistId}`);
      const data: SavedPlaylist | { error?: string } = await response.json();
      if (!response.ok || !('songs' in data)) {
        throw new Error((data as any)?.error || `HTTP ${response.status}`);
      }

      const { tracks, skippedCount } = mapSavedSongsToTracks(data.songs || []);
      setSelectedPlaylistTracks(tracks);
      if (skippedCount > 0) {
        setPlaylistStatus(`${skippedCount} track(s) zonder bruikbare audio-link zichtbaar gemaakt (nog niet afspeelbaar)`);
      }
    } catch (error) {
      setSelectedPlaylistTracks([]);
      const message = error instanceof Error ? error.message : 'Onbekende fout';
      setPlaylistStatus(`Afspeellijst nummers laden mislukt: ${message}`);
    }
  }, [mapSavedSongsToTracks]);

  const refreshServerLibrary = useCallback(async () => {
    setLibraryBusy(true);
    setLibraryStatus(null);
    try {
      const response = await fetch(`${API_BASE}/api/dj/music-library`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      const folders: DjMusicFolder[] = Array.isArray(data?.folders) ? data.folders : [];
      const serverTracks: DjTrack[] = Array.isArray(data?.tracks)
        ? data.tracks.map((track: any) => ({
            id: String(track.id || `server-${Math.random()}`),
            title: String(track.title || 'Onbekend nummer'),
            artist: String(track.artist || 'Onbekende artiest'),
            audioUrl: String(track.audioUrl || ''),
            sourceFolderId: String(track.sourceFolderId || ''),
            source: 'server' as const,
          })).filter((track: DjTrack) => track.audioUrl.length > 0)
        : [];

      setMusicFolders(folders);
      setSelectedMusicFolderId((current) => {
        if (current && folders.some((folder: DjMusicFolder) => folder.id === current)) {
          return current;
        }
        return folders[0]?.id || null;
      });
      setLibrary((current) => {
        const withoutServer = current.filter((track) => track.source !== 'server');
        return [...withoutServer, ...serverTracks];
      });

      setLibraryStatus(`${serverTracks.length} server tracks geladen uit ${folders.length} map(pen)`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Onbekende fout';
      setLibraryStatus(`Laden mislukt: ${message}`);
    } finally {
      setLibraryBusy(false);
    }
  }, []);

  const removeMusicFolder = useCallback(async (folderId: string) => {
    setLibraryBusy(true);
    setLibraryStatus(null);
    try {
      const response = await fetch(`${API_BASE}/api/dj/music-folders/${encodeURIComponent(folderId)}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      await refreshServerLibrary();
      setLibraryStatus('Map verwijderd');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Onbekende fout';
      setLibraryStatus(`Map verwijderen mislukt: ${message}`);
    } finally {
      setLibraryBusy(false);
    }
  }, [refreshServerLibrary]);

  useEffect(() => {
    loadSavedPlaylists();
    loadDashboardPlaylists();
    refreshServerLibrary();
  }, [loadDashboardPlaylists, loadSavedPlaylists, refreshServerLibrary]);

  useEffect(() => {
    if (!selectedSavedPlaylistId) return;
    const selected = savedPlaylists.find((item) => item.id === selectedSavedPlaylistId);
    if (selected) {
      setSavedPlaylistName(selected.name);
    }
  }, [savedPlaylists, selectedSavedPlaylistId]);

  useEffect(() => {
    if (!selectedSavedPlaylistId) {
      setSelectedPlaylistTracks([]);
      return;
    }

    loadSelectedPlaylistTracks(selectedSavedPlaylistId);
  }, [loadSelectedPlaylistTracks, selectedSavedPlaylistId]);

  const filteredPlaylistTracks = useMemo(() => {
    const search = playlistTrackSearch.trim().toLowerCase();
    return selectedPlaylistTracks.filter((track) => {
      const matchesSource = playlistTrackSourceFilter === 'all' || track.source === playlistTrackSourceFilter;
      if (!matchesSource) return false;
      if (!search) return true;

      const haystack = `${track.title} ${track.artist}`.toLowerCase();
      return haystack.includes(search);
    });
  }, [playlistTrackSearch, playlistTrackSourceFilter, selectedPlaylistTracks]);

  const folderLibraryTracks = useMemo(() => {
    if (!selectedMusicFolderId) return [];
    return library.filter((track) => track.sourceFolderId === selectedMusicFolderId);
  }, [library, selectedMusicFolderId]);

  const getSavableTracks = useCallback(() => {
    const savable = selectedPlaylistTracks.filter((track) => hasStableAudioUrl(track.audioUrl));
    const skippedCount = selectedPlaylistTracks.length - savable.length;

    return {
      songs: savable.map((track) => ({
        id: track.id,
        title: track.title,
        artist: track.artist,
        year: null,
        audioUrl: track.audioUrl,
        imageUrl: track.imageUrl || '',
        youtubeVideoId: '',
        questions: [],
      })),
      skippedCount,
    };
  }, [selectedPlaylistTracks]);

  const createSavedPlaylist = useCallback(async (preferredName?: string) => {
    const name = String(preferredName ?? savedPlaylistName).trim() || buildAutoPlaylistName();
    if (!savedPlaylistName.trim()) {
      setSavedPlaylistName(name);
    }

    const { songs, skippedCount } = getSavableTracks();
    if (songs.length === 0) {
      setPlaylistStatus('Geen opslaanbare tracks: lokale blob-bestanden kunnen niet persistent worden opgeslagen');
      return;
    }

    setPlaylistBusy(true);
    setPlaylistStatus(null);
    try {
      const response = await fetch(`${API_BASE}/api/saved-playlists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, songs, songsPerRound: 10 }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      setPlaylistStatus(
        skippedCount > 0
          ? `Afspeellijst opgeslagen (${songs.length} tracks, ${skippedCount} lokale blob-tracks overgeslagen)`
          : 'Afspeellijst opgeslagen op de server'
      );
      await loadSavedPlaylists();
      if (typeof data.id === 'number') {
        setSelectedSavedPlaylistId(data.id);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Onbekende fout';
      setPlaylistStatus(`Opslaan mislukt: ${message}`);
    } finally {
      setPlaylistBusy(false);
    }
  }, [getSavableTracks, loadSavedPlaylists, savedPlaylistName]);

  const updateSavedPlaylistById = useCallback(async () => {
    if (!selectedSavedPlaylistId) {
      setPlaylistStatus('Selecteer eerst een bestaande afspeellijst');
      return;
    }

    const name = savedPlaylistName.trim();
    if (!name) {
      setPlaylistStatus('Geef een naam voor de afspeellijst');
      return;
    }

    const { songs, skippedCount } = getSavableTracks();
    if (songs.length === 0) {
      setPlaylistStatus('Geen opslaanbare tracks: lokale blob-bestanden kunnen niet persistent worden opgeslagen');
      return;
    }

    setPlaylistBusy(true);
    setPlaylistStatus(null);
    try {
      const response = await fetch(`${API_BASE}/api/saved-playlists/${selectedSavedPlaylistId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, songs, songsPerRound: 10 }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      setPlaylistStatus(
        skippedCount > 0
          ? `Afspeellijst bijgewerkt (${songs.length} tracks, ${skippedCount} lokale blob-tracks overgeslagen)`
          : 'Afspeellijst bijgewerkt'
      );
      await loadSavedPlaylists();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Onbekende fout';
      setPlaylistStatus(`Bijwerken mislukt: ${message}`);
    } finally {
      setPlaylistBusy(false);
    }
  }, [getSavableTracks, loadSavedPlaylists, savedPlaylistName, selectedSavedPlaylistId]);

  const loadSavedPlaylistById = useCallback(async () => {
    if (!selectedSavedPlaylistId) {
      setPlaylistStatus('Selecteer eerst een afspeellijst');
      return;
    }

    setPlaylistBusy(true);
    setPlaylistStatus(null);
    try {
      const response = await fetch(`${API_BASE}/api/saved-playlists/${selectedSavedPlaylistId}`);
      const data: SavedPlaylist | { error?: string } = await response.json();
      if (!response.ok || !('songs' in data)) {
        throw new Error((data as any)?.error || `HTTP ${response.status}`);
      }

      const { tracks: savedTracks, skippedCount } = mapSavedSongsToTracks(data.songs || []);

      setLibrary((current) => {
        const withoutSaved = current.filter((track) => track.source !== 'saved');
        return [...withoutSaved, ...savedTracks];
      });

      setDeckA(initialDeckState());
      setDeckB(initialDeckState());
      setSavedPlaylistName(data.name || '');
      setSelectedPlaylistTracks(savedTracks);
      setPlaylistStatus(
        skippedCount > 0
          ? `Afspeellijst geladen: ${data.name} (${skippedCount} track(s) zonder bruikbare audio-link zichtbaar, maar niet afspeelbaar)`
          : `Afspeellijst geladen: ${data.name}`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Onbekende fout';
      setPlaylistStatus(`Laden mislukt: ${message}`);
    } finally {
      setPlaylistBusy(false);
    }
  }, [mapSavedSongsToTracks, selectedSavedPlaylistId]);

  const resolveDashboardSongAudio = useCallback(async (song: SavedPlaylist['songs'][number]) => {
    const existingAudio = String(song?.audioUrl || song?.previewUrl || '').trim();
    if (existingAudio) {
      return { ...song, audioUrl: existingAudio };
    }

    const spotifyId = String(song?.spotifyId || '').trim() || String(song?.spotifyUri || '').replace('spotify:track:', '').trim();
    if (spotifyId) {
      try {
        const response = await fetch(`${API_BASE}/api/spotify/track/${encodeURIComponent(spotifyId)}`);
        if (response.ok) {
          const data = await response.json();
          const previewUrl = String(data?.previewUrl || '').trim();
          if (previewUrl) {
            return {
              ...song,
              audioUrl: previewUrl,
              previewUrl,
            };
          }
        }
      } catch {
        // Ignore lookup errors and continue with next fallback.
      }
    }

    const youtubeVideoId = String(song?.youtubeVideoId || '').trim();
    if (youtubeVideoId) {
      try {
        const response = await fetch(`${API_BASE}/api/youtube/video/${encodeURIComponent(youtubeVideoId)}`);
        if (response.ok) {
          const data = await response.json();
          const audioUrl = String(data?.audioUrl || '').trim();
          if (audioUrl) {
            return {
              ...song,
              audioUrl,
            };
          }
        }
      } catch {
        // Ignore lookup errors.
      }
    }

    return song;
  }, []);

  const loadDashboardPlaylistById = useCallback(async () => {
    if (!selectedDashboardPlaylistId) {
      setPlaylistStatus('Selecteer eerst een dashboard-afspeellijst');
      return;
    }

    const selected = dashboardPlaylists.find((item) => item.id === selectedDashboardPlaylistId);
    if (!selected) {
      setPlaylistStatus('Gekozen dashboard-afspeellijst niet gevonden');
      return;
    }

    setPlaylistBusy(true);
    setPlaylistStatus(null);

    try {
      const enrichedSongs = await Promise.all((selected.songs || []).map((song) => resolveDashboardSongAudio(song)));
      const { tracks, skippedCount } = mapSavedSongsToTracks(enrichedSongs);

      setLibrary((current) => {
        const withoutSaved = current.filter((track) => track.source !== 'saved');
        return [...withoutSaved, ...tracks];
      });

      setDeckA(initialDeckState());
      setDeckB(initialDeckState());
      setSelectedPlaylistTracks(tracks);
      setSelectedSavedPlaylistId(null);
      setSavedPlaylistName(selected.name);
      setPlaylistStatus(
        skippedCount > 0
          ? `Dashboard-afspeellijst geladen: ${selected.name} (${skippedCount} nummer(s) zonder bruikbare audio-link zichtbaar, maar niet afspeelbaar)`
          : `Dashboard-afspeellijst geladen: ${selected.name}`
      );
    } finally {
      setPlaylistBusy(false);
    }
  }, [dashboardPlaylists, mapSavedSongsToTracks, resolveDashboardSongAudio, selectedDashboardPlaylistId]);

  const updateDeck = useCallback((deck: DeckKey, updater: (state: DeckState) => DeckState) => {
    if (deck === 'A') {
      setDeckA((state) => updater(state));
      return;
    }
    setDeckB((state) => updater(state));
  }, []);

  const getDeckState = useCallback((deck: DeckKey): DeckState => {
    return deck === 'A' ? deckAStateRef.current : deckBStateRef.current;
  }, []);

  const getAudio = useCallback((deck: DeckKey): HTMLAudioElement | null => {
    return deck === 'A' ? deckARef.current : deckBRef.current;
  }, []);

  const applyMixVolumes = useCallback(() => {
    const audioA = deckARef.current;
    const audioB = deckBRef.current;
    if (!audioA || !audioB) return;

    const gainA = Math.cos(crossfader * Math.PI / 2);
    const gainB = Math.sin(crossfader * Math.PI / 2);
    const effectiveA = clamp(deckAStateRef.current.volume * masterVolume * gainA, 0, 1);
    const effectiveB = clamp(deckBStateRef.current.volume * masterVolume * gainB, 0, 1);
    audioA.volume = effectiveA;
    audioB.volume = effectiveB;

    const meter = Math.max(effectiveA * (audioA.paused ? 0 : 1), effectiveB * (audioB.paused ? 0 : 1));
    setVuLevel(meter);
  }, [crossfader, masterVolume]);

  const loadTrack = useCallback((deck: DeckKey, index: number, autoplay: boolean) => {
    const state = getDeckState(deck);
    const track = state.queue[index];
    const audio = getAudio(deck);
    if (!track || !audio) return;

    audio.src = track.audioUrl;
    audio.load();
    updateDeck(deck, (current) => ({
      ...current,
      loadedIndex: index,
      status: autoplay ? 'Speelt' : 'Geladen',
      currentTime: 0,
    }));

    if (autoplay) {
      audio.play().catch(() => {
        updateDeck(deck, (current) => ({ ...current, status: 'Klik op afspelen' }));
      });
    }
  }, [getAudio, getDeckState, updateDeck]);

  const stopDeck = useCallback((deck: DeckKey) => {
    const audio = getAudio(deck);
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    updateDeck(deck, (state) => ({
      ...state,
      isPlaying: false,
      status: 'Stil',
      currentTime: 0,
    }));
    applyMixVolumes();
  }, [applyMixVolumes, getAudio, updateDeck]);

  const playPauseDeck = useCallback((deck: DeckKey) => {
    const state = getDeckState(deck);
    const audio = getAudio(deck);
    if (!audio) return;

    const intendedIndex = state.loadedIndex ?? 0;
    const intendedTrack = state.queue[intendedIndex];

    if (intendedTrack) {
      const expectedSrc = new URL(intendedTrack.audioUrl, window.location.origin).href;
      const activeSrc = audio.currentSrc || audio.src;
      if (!activeSrc || activeSrc !== expectedSrc) {
        loadTrack(deck, intendedIndex, true);
        return;
      }
    }

    if (!audio.src) {
      if (state.queue.length === 0) {
        updateDeck(deck, (current) => ({ ...current, status: 'Geen track in wachtrij' }));
        return;
      }
      loadTrack(deck, 0, true);
      return;
    }

    if (audio.paused) {
      audio.play().catch(() => {
        updateDeck(deck, (current) => ({ ...current, status: 'Afspelen mislukt' }));
      });
    } else {
      audio.pause();
    }
  }, [getAudio, getDeckState, loadTrack, updateDeck]);

  const queueTrackToDeck = useCallback((deck: DeckKey, track: DjTrack) => {
    if (!hasStableAudioUrl(track.audioUrl)) {
      setPlaylistStatus(`Track "${track.title}" heeft geen bruikbare audio-link en kan niet naar Deck ${deck}`);
      return;
    }

    let shouldLoadAfterQueue = false;

    updateDeck(deck, (state) => {
      const alreadyInQueue = state.queue.some((item) => item.id === track.id && item.audioUrl === track.audioUrl);
      const nextQueue = alreadyInQueue ? state.queue : [...state.queue, track];
      const shouldLoadFirst = state.loadedIndex === null && nextQueue.length > 0;
      shouldLoadAfterQueue = shouldLoadFirst;
      return {
        ...state,
        queue: nextQueue,
        loadedIndex: shouldLoadFirst ? 0 : state.loadedIndex,
      };
    });

    if (shouldLoadAfterQueue) {
      window.setTimeout(() => loadTrack(deck, 0, false), 0);
    }
  }, [loadTrack, updateDeck]);

  const replaceDeckQueueFromPlaylist = useCallback((deck: DeckKey, tracks: DjTrack[]) => {
    if (tracks.length === 0) return;

    const firstTrack = tracks[0];
    const audio = getAudio(deck);
    if (audio) {
      audio.pause();
      audio.src = firstTrack.audioUrl;
      audio.load();
      audio.currentTime = 0;
    }

    updateDeck(deck, (state) => ({
      ...state,
      queue: tracks,
      loadedIndex: 0,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      status: 'Geladen',
    }));

    applyMixVolumes();
  }, [applyMixVolumes, getAudio, updateDeck]);

  const queueFilteredPlaylistTracks = useCallback((target: 'A' | 'B' | 'split') => {
    if (selectedPlaylistTracks.length === 0) {
      setPlaylistStatus('Selecteer eerst een afspeellijst');
      return;
    }

    if (filteredPlaylistTracks.length === 0) {
      setPlaylistStatus('Geen nummers gevonden voor bulk toevoegen');
      return;
    }

    const playableTracks = filteredPlaylistTracks.filter((track) => hasStableAudioUrl(track.audioUrl));
    const skippedTracks = filteredPlaylistTracks.length - playableTracks.length;

    if (playableTracks.length === 0) {
      setPlaylistStatus('Geen afspeelbare nummers in deze selectie (alle tracks missen een audio-link)');
      return;
    }

    if (target === 'A' || target === 'B') {
      replaceDeckQueueFromPlaylist(target, playableTracks);
      setPlaylistStatus(
        skippedTracks > 0
          ? `${playableTracks.length} nummer(s) geladen op Deck ${target} vanaf track 1 (${skippedTracks} zonder audio-link overgeslagen)`
          : `${playableTracks.length} nummer(s) geladen op Deck ${target} vanaf track 1`
      );
      return;
    }

    playableTracks.forEach((track, index) => {
      queueTrackToDeck(index % 2 === 0 ? 'A' : 'B', track);
    });
    setPlaylistStatus(
      skippedTracks > 0
        ? `${playableTracks.length} nummer(s) om en om verdeeld over Deck A en B (${skippedTracks} zonder audio-link overgeslagen)`
        : `${playableTracks.length} nummer(s) om en om verdeeld over Deck A en B`
    );
  }, [filteredPlaylistTracks, queueTrackToDeck, replaceDeckQueueFromPlaylist, selectedPlaylistTracks.length]);

  const repairSelectedPlaylistAudioLinks = useCallback(async () => {
    if (selectedPlaylistTracks.length === 0) {
      setPlaylistStatus('Selecteer eerst een afspeellijst');
      return;
    }

    const missingCount = selectedPlaylistTracks.filter((track) => !hasStableAudioUrl(track.audioUrl)).length;
    if (missingCount === 0) {
      setPlaylistStatus('Alle nummers in deze lijst hebben al een audio-link');
      return;
    }

    setPlaylistBusy(true);
    setPlaylistStatus(`Audio-links herstellen gestart voor ${missingCount} nummer(s)...`);

    let restoredSpotify = 0;
    let restoredYoutube = 0;
    let processedMissing = 0;

    try {
      const repairedTracks: DjTrack[] = [];

      for (const track of selectedPlaylistTracks) {
        if (hasStableAudioUrl(track.audioUrl)) {
          repairedTracks.push(track);
          continue;
        }

        processedMissing += 1;
        if (processedMissing % 15 === 0 || processedMissing === 1) {
          setPlaylistStatus(`Audio-links herstellen... ${processedMissing}/${missingCount}`);
        }

        let repairedTrack = track;
        const query = encodeURIComponent(`${track.artist} ${track.title}`.trim());

        // 1) Try Spotify preview URL.
        try {
          const spotifyResponse = await fetch(`${API_BASE}/api/spotify/search?query=${query}&type=track&limit=8`);
          if (spotifyResponse.ok) {
            const spotifyData = await spotifyResponse.json();
            const spotifyResults = Array.isArray(spotifyData?.results) ? spotifyData.results : [];
            const bestWithPreview = spotifyResults.find((item: any) => typeof item?.previewUrl === 'string' && item.previewUrl.trim().length > 0);
            if (bestWithPreview?.previewUrl) {
              restoredSpotify += 1;
              repairedTrack = {
                ...track,
                audioUrl: String(bestWithPreview.previewUrl),
              };
            }
          }
        } catch {
          // Continue with YouTube fallback.
        }

        // 2) Fallback: find YouTube match and download audio.
        if (!hasStableAudioUrl(repairedTrack.audioUrl)) {
          try {
            const ytMatchResponse = await fetch(
              `${API_BASE}/api/spotify/youtube-match?artist=${encodeURIComponent(track.artist)}&title=${encodeURIComponent(track.title)}`
            );

            if (ytMatchResponse.ok) {
              const ytMatchData = await ytMatchResponse.json();
              const ytUrl = Array.isArray(ytMatchData?.results) && ytMatchData.results[0]?.url
                ? String(ytMatchData.results[0].url)
                : '';

              if (ytUrl) {
                const downloadResponse = await fetch(`${API_BASE}/api/youtube/download`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    url: ytUrl,
                    title: `${track.artist} - ${track.title}`.slice(0, 60),
                  }),
                });

                if (downloadResponse.ok) {
                  const downloadData = await downloadResponse.json();
                  const downloadedAudioUrl = String(downloadData?.url || '').trim();
                  if (downloadedAudioUrl) {
                    restoredYoutube += 1;
                    repairedTrack = {
                      ...track,
                      audioUrl: downloadedAudioUrl,
                    };
                  }
                }
              }
            }
          } catch {
            // Keep track unresolved when download fails.
          }
        }

        repairedTracks.push(repairedTrack);
      }

      setSelectedPlaylistTracks(repairedTracks);
      setLibrary((current) => {
        const nonSaved = current.filter((track) => track.source !== 'saved');
        const savedTracks = repairedTracks.filter((track) => track.source === 'saved');
        return savedTracks.length > 0 ? [...nonSaved, ...savedTracks] : current;
      });

      const remainingMissing = repairedTracks.filter((track) => !hasStableAudioUrl(track.audioUrl)).length;
      const totalRestored = restoredSpotify + restoredYoutube;
      setPlaylistStatus(
        totalRestored > 0
          ? `Audio-links hersteld: ${totalRestored} (Spotify ${restoredSpotify}, YouTube ${restoredYoutube}). Nog ${remainingMissing} zonder audio-link.`
          : `Geen audio-links gevonden via Spotify/YouTube voor deze selectie (nog ${remainingMissing} zonder audio-link).`
      );
    } finally {
      setPlaylistBusy(false);
    }
  }, [selectedPlaylistTracks]);

  const addTrackToWorkingPlaylist = useCallback((track: DjTrack) => {
    setSelectedPlaylistTracks((current) => {
      const alreadyExists = current.some((item) => item.id === track.id && item.audioUrl === track.audioUrl);
      if (alreadyExists) return current;
      return [...current, { ...track, source: track.source === 'server' ? 'saved' : track.source }];
    });
    setPlaylistStatus(`"${track.title}" toegevoegd aan afspeellijst`);
  }, []);

  const removeTrackFromWorkingPlaylist = useCallback((trackId: string) => {
    setSelectedPlaylistTracks((current) => current.filter((track) => track.id !== trackId));
  }, []);

  const addFilesToDeck = useCallback((deck: DeckKey, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const uploadedTracks: DjTrack[] = Array.from(files).map((file, idx) => ({
      id: `upload-${deck}-${Date.now()}-${idx}`,
      title: file.name.replace(/\.[^/.]+$/, ''),
      artist: 'Lokale upload',
      audioUrl: URL.createObjectURL(file),
      source: 'upload',
    }));

    updateDeck(deck, (state) => {
      const queue = [...state.queue, ...uploadedTracks];
      return {
        ...state,
        queue,
        loadedIndex: state.loadedIndex === null && queue.length > 0 ? 0 : state.loadedIndex,
      };
    });
  }, [updateDeck]);

  const addFilesToLibrary = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const uploadedTracks: DjTrack[] = Array.from(files).map((file, idx) => ({
      id: `library-upload-${Date.now()}-${idx}`,
      title: file.name.replace(/\.[^/.]+$/, ''),
      artist: 'Lokale upload',
      audioUrl: URL.createObjectURL(file),
      source: 'upload',
    }));

    setLibrary((state) => [...state, ...uploadedTracks]);
  }, []);

  const crossfadeToDeck = useCallback((targetDeck: DeckKey, durationMs = 6000) => {
    if (autoMixingRef.current) return;
    autoMixingRef.current = true;

    const sourceDeck: DeckKey = targetDeck === 'A' ? 'B' : 'A';
    const sourceAudio = getAudio(sourceDeck);
    const targetAudio = getAudio(targetDeck);

    if (!sourceAudio || !targetAudio) {
      autoMixingRef.current = false;
      return;
    }

    const start = performance.now();
    const from = targetDeck === 'A' ? 1 : 0;
    const to = targetDeck === 'A' ? 0 : 1;

    const tick = (now: number) => {
      const progress = clamp((now - start) / durationMs, 0, 1);
      const nextValue = from + (to - from) * progress;
      setCrossfader(nextValue);

      if (progress < 1) {
        requestAnimationFrame(tick);
        return;
      }

      if (!sourceAudio.paused) {
        sourceAudio.pause();
        sourceAudio.currentTime = 0;
      }
      setActiveDeck(targetDeck);
      updateDeck(sourceDeck, (state) => ({ ...state, isPlaying: false, status: 'Stil', currentTime: 0 }));
      autoMixingRef.current = false;
    };

    requestAnimationFrame(tick);
  }, [getAudio, updateDeck]);

  useEffect(() => {
    applyMixVolumes();
  }, [applyMixVolumes, crossfader, masterVolume, deckA.volume, deckB.volume, deckA.isPlaying, deckB.isPlaying]);

  useEffect(() => {
    const bindDeck = (deck: DeckKey, audio: HTMLAudioElement, setter: React.Dispatch<React.SetStateAction<DeckState>>) => {
      const onPlay = () => {
        setter((state) => ({ ...state, isPlaying: true, status: 'Speelt' }));
        applyMixVolumes();
      };
      const onPause = () => {
        setter((state) => ({
          ...state,
          isPlaying: false,
          status: state.currentTime > 0 && state.currentTime < state.duration ? 'Gepauzeerd' : state.status,
        }));
        applyMixVolumes();
      };
      const onLoaded = () => {
        setter((state) => ({ ...state, duration: Number.isFinite(audio.duration) ? audio.duration : 0 }));
      };
      const onTime = () => {
        setter((state) => ({
          ...state,
          currentTime: audio.currentTime,
          duration: Number.isFinite(audio.duration) ? audio.duration : state.duration,
        }));
      };
      const onError = () => {
        setter((state) => ({
          ...state,
          isPlaying: false,
          status: 'Bestand niet beschikbaar',
        }));
      };
      const onEnded = () => {
        setter((state) => {
          const nextIndex = state.loadedIndex !== null ? state.loadedIndex + 1 : null;
          if (nextIndex !== null && nextIndex < state.queue.length) {
            setTimeout(() => loadTrack(deck, nextIndex, true), 50);
            return { ...state, isPlaying: false, status: 'Volgende track laden' };
          }
          return {
            ...state,
            isPlaying: false,
            currentTime: 0,
            status: 'Klaar',
          };
        });
      };

      audio.addEventListener('play', onPlay);
      audio.addEventListener('pause', onPause);
      audio.addEventListener('loadedmetadata', onLoaded);
      audio.addEventListener('timeupdate', onTime);
      audio.addEventListener('error', onError);
      audio.addEventListener('ended', onEnded);

      return () => {
        audio.removeEventListener('play', onPlay);
        audio.removeEventListener('pause', onPause);
        audio.removeEventListener('loadedmetadata', onLoaded);
        audio.removeEventListener('timeupdate', onTime);
        audio.removeEventListener('error', onError);
        audio.removeEventListener('ended', onEnded);
      };
    };

    const audioA = deckARef.current;
    const audioB = deckBRef.current;
    if (!audioA || !audioB) return;

    const cleanA = bindDeck('A', audioA, setDeckA);
    const cleanB = bindDeck('B', audioB, setDeckB);
    return () => {
      cleanA();
      cleanB();
    };
  }, [applyMixVolumes, loadTrack]);

  useEffect(() => {
    if (!autoDjEnabled) return;

    const timer = window.setInterval(() => {
      const current = activeDeck === 'A' ? deckAStateRef.current : deckBStateRef.current;
      const currentAudio = activeDeck === 'A' ? deckARef.current : deckBRef.current;
      const nextDeck: DeckKey = activeDeck === 'A' ? 'B' : 'A';
      const next = nextDeck === 'A' ? deckAStateRef.current : deckBStateRef.current;
      const nextAudio = nextDeck === 'A' ? deckARef.current : deckBRef.current;

      if (!currentAudio || !nextAudio) return;

      if (!currentAudio.src) {
        if (current.queue.length > 0) {
          loadTrack(activeDeck, current.loadedIndex ?? 0, true);
        }
        return;
      }

      if (!current.isPlaying && current.queue.length > 0) {
        currentAudio.play().catch(() => {});
        return;
      }

      const remaining = (current.duration || 0) - (current.currentTime || 0);
      if (remaining > 8 || autoMixingRef.current) {
        return;
      }

      if (next.queue.length === 0) {
        return;
      }

      const nextIndex = next.loadedIndex ?? 0;
      if (!nextAudio.src) {
        loadTrack(nextDeck, nextIndex, true);
      } else if (nextAudio.paused) {
        nextAudio.play().catch(() => {});
      }

      crossfadeToDeck(nextDeck, 6000);
    }, 900);

    return () => window.clearInterval(timer);
  }, [activeDeck, autoDjEnabled, crossfadeToDeck, loadTrack]);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/radiojar/status`);
        if (!response.ok) return;

        const data = await readResponse(response);
        if (data.config) {
          setRadiojarConfig({
            host: data.config.host || DEFAULT_RADIOJAR_CONFIG.host,
            port: String(data.config.port || DEFAULT_RADIOJAR_CONFIG.port),
            encoding: String(data.config.encoding || DEFAULT_RADIOJAR_CONFIG.encoding),
            mountPoint: data.config.mountPoint || DEFAULT_RADIOJAR_CONFIG.mountPoint,
            username: data.config.username || DEFAULT_RADIOJAR_CONFIG.username,
            password: data.config.password || DEFAULT_RADIOJAR_CONFIG.password,
          });
        }
        if (data.connected) {
          setRadiojarConnected(true);
          setRadiojarStatus('Live verbonden');
        }
      } catch {
        setRadiojarStatus('Status niet geladen');
      }
    };

    loadStatus();
  }, []);

  const connectRadiojar = useCallback(async () => {
    setRadiojarLoading(true);
    setRadiojarError(null);
    try {
      const response = await fetch(`${API_BASE}/api/radiojar/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(radiojarConfig),
      });

      const data = await readResponse(response);
      const detail = [data.error, data.transportError, data.details].filter(Boolean).join(' | ');
      if (!response.ok || !data.connected) {
        throw new Error(detail || `Radiojar returned HTTP ${response.status}`);
      }

      setRadiojarConnected(true);
      setRadiojarStatus(data.message || 'Live verbonden');
      if (data.keepAlive === false) {
        const keepAliveWarning = data.keepAliveWarning || 'Handshake gelukt, maar keepalive stream kon niet actief blijven.';
        setRadiojarError(keepAliveWarning);
      } else {
        setRadiojarError(null);
      }

      try {
        await startBrowserBroadcast();
      } catch (broadcastError) {
        const message = broadcastError instanceof Error ? broadcastError.message : 'Browser broadcast kon niet starten';
        setRadiojarError(message);
        setRadiojarStatus('Live verbonden, maar browser-broadcast faalt');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Onbekende fout';
      setRadiojarConnected(false);
      setRadiojarStatus('Verbinding mislukt');
      setRadiojarError(message);
    } finally {
      setRadiojarLoading(false);
    }
  }, [radiojarConfig, startBrowserBroadcast]);

  const stopRadiojar = useCallback(async () => {
    setRadiojarLoading(true);
    setRadiojarError(null);
    try {
      await stopBrowserBroadcast();
      const response = await fetch(`${API_BASE}/api/radiojar/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await readResponse(response);
      if (!response.ok) {
        throw new Error(data.error || `Radiojar returned HTTP ${response.status}`);
      }

      setRadiojarConnected(false);
      setRadiojarStatus(data.message || 'Stream gestopt');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Onbekende fout';
      setRadiojarStatus('Stoppen mislukt');
      setRadiojarError(message);
    } finally {
      setRadiojarLoading(false);
    }
  }, [stopBrowserBroadcast]);

  useEffect(() => {
    return () => {
      void stopBrowserBroadcast();
    };
  }, [stopBrowserBroadcast]);

  const startFullPlaylistAutoplay = useCallback(() => {
    const playableTracks = library.filter((track) => hasStableAudioUrl(track.audioUrl));
    if (playableTracks.length === 0) {
      setPlaylistStatus('Bibliotheek is leeg, voeg eerst nummers toe');
      return;
    }

    const queueA: DjTrack[] = [];
    const queueB: DjTrack[] = [];

    playableTracks.forEach((track, index) => {
      if (index % 2 === 0) {
        queueA.push(track);
      } else {
        queueB.push(track);
      }
    });

    setDeckA((state) => ({
      ...state,
      queue: queueA,
      loadedIndex: queueA.length > 0 ? 0 : null,
      currentTime: 0,
      duration: 0,
      status: queueA.length > 0 ? 'Geladen' : 'Stil',
    }));

    setDeckB((state) => ({
      ...state,
      queue: queueB,
      loadedIndex: queueB.length > 0 ? 0 : null,
      currentTime: 0,
      duration: 0,
      status: queueB.length > 0 ? 'Geladen' : 'Stil',
    }));

    setActiveDeck('A');
    setCrossfader(0);
    setAutoDjEnabled(true);
    setPlaylistStatus(
      playableTracks.length === library.length
        ? `Auto DJ gestart met ${playableTracks.length} tracks`
        : `Auto DJ gestart met ${playableTracks.length} afspeelbare tracks (${library.length - playableTracks.length} zonder audio-link overgeslagen)`
    );

    setTimeout(() => {
      if (queueA.length > 0) {
        loadTrack('A', 0, true);
      }
      if (queueB.length > 0) {
        loadTrack('B', 0, false);
      }
    }, 60);
  }, [library, loadTrack]);

  const renderDeck = (deck: DeckKey, state: DeckState) => {
    const loadedTrack = state.loadedIndex !== null ? state.queue[state.loadedIndex] : null;
    const progress = state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0;

    return (
      <div className={`dj-deck ${deck === 'A' ? 'deck-a' : 'deck-b'}`}>
        <div className="dj-deck-label">
          <b>Deck {deck}</b>
          <span>{state.status}</span>
        </div>

        <div className="dj-now-playing">
          {loadedTrack ? (
            <>
              <div className="dj-now-title">{loadedTrack.title}</div>
              <div className="dj-now-artist">{loadedTrack.artist}</div>
            </>
          ) : (
            <div className="dj-empty">Geen nummer geladen</div>
          )}
        </div>

        <div className="dj-progress-wrap">
          <input
            type="range"
            min={0}
            max={100}
            value={progress}
            onChange={(event) => {
              const audio = getAudio(deck);
              if (!audio || !state.duration) return;
              const next = (Number(event.target.value) / 100) * state.duration;
              audio.currentTime = next;
              updateDeck(deck, (current) => ({ ...current, currentTime: next }));
            }}
          />
        </div>

        <div className="dj-times">
          <span>{fmtTime(state.currentTime)}</span>
          <span>{fmtTime(state.duration)}</span>
        </div>

        <div className="dj-transport">
          <button className={`dj-play-btn ${state.isPlaying ? 'is-active' : ''}`} onClick={() => playPauseDeck(deck)}>
            {state.isPlaying ? 'Pauze' : 'Afspelen'}
          </button>
          <button onClick={() => stopDeck(deck)}>Stop</button>
        </div>

        <div className="dj-volume-row">
          <span>Volume</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={state.volume}
            onChange={(event) => {
              const nextVolume = Number(event.target.value);
              updateDeck(deck, (current) => ({ ...current, volume: nextVolume }));
            }}
          />
        </div>

        <div className="dj-queue-head">
          <label>Wachtrij</label>
          <button onClick={() => (deck === 'A' ? uploadARef.current?.click() : uploadBRef.current?.click())}>+ Nummers</button>
          <input
            type="file"
            hidden
            multiple
            accept="audio/*"
            ref={deck === 'A' ? uploadARef : uploadBRef}
            onChange={(event) => {
              addFilesToDeck(deck, event.target.files);
              event.target.value = '';
            }}
          />
        </div>

        <ul className="dj-queue">
          {state.queue.length === 0 && <li className="dj-queue-empty">Nog geen nummers toegevoegd</li>}
          {state.queue.map((track, index) => (
            <li
              key={`${track.id}-${index}`}
              className={state.loadedIndex === index ? 'loaded' : ''}
              onClick={() => loadTrack(deck, index, true)}
            >
              <span>{track.title} - {track.artist}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="dj-page">
      <audio ref={deckARef} preload="metadata" />
      <audio ref={deckBRef} preload="metadata" />

      <div className="dj-shell">
        <header className="dj-header">
          <div className="dj-brand">
            <div className="dj-nav-links">
              <button className="dj-back" onClick={() => navigate('/spotify-dashboard')}>Terug</button>
              <button className="dj-back" onClick={() => navigate('/prepare')}>Playlist voorbereiden</button>
            </div>
            <div>
              <div className="dj-name">Remotion Radio DJ Console</div>
              <div className="dj-sub">2 Decks Auto DJ</div>
            </div>
          </div>
          <div className="dj-clock">{clock}</div>
        </header>

        <div className="dj-grid">
          {renderDeck('A', deckA)}

          <div className="dj-mixer">
            <div className={`dj-onair ${radiojarConnected ? 'live' : ''}`}>ON AIR</div>
            <div className="dj-vu-track">
              <div className="dj-vu-fill" style={{ width: `${Math.round(vuLevel * 100)}%` }} />
            </div>

            <div className="dj-mixer-group">
              <div className="dj-row-label"><span>A</span><span>B</span></div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={crossfader}
                onChange={(event) => setCrossfader(Number(event.target.value))}
              />
            </div>

            <div className="dj-mixer-group">
              <div className="dj-row-label"><span>Master</span><span>{Math.round(masterVolume * 100)}%</span></div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={masterVolume}
                onChange={(event) => setMasterVolume(Number(event.target.value))}
              />
            </div>

            <div className="dj-mixer-group">
              <div className="dj-row-label"><span>Auto DJ</span><span>{autoDjEnabled ? 'AAN' : 'UIT'}</span></div>
              <button
                className={`dj-auto-btn ${autoDjEnabled ? 'active' : ''}`}
                onClick={() => setAutoDjEnabled((state) => !state)}
              >
                {autoDjEnabled ? 'Auto DJ stoppen' : 'Auto DJ starten'}
              </button>
              <button className="dj-autoplay-all-btn" onClick={startFullPlaylistAutoplay}>
                Start volledige afspeellijst
              </button>
            </div>

            <div className="dj-send-buttons">
              <button
                onClick={() => {
                  const selected = library.find((track) => track.id === selectedLibraryTrackId);
                  if (selected) queueTrackToDeck('A', selected);
                }}
              >
                Naar A
              </button>
              <button
                onClick={() => {
                  const selected = library.find((track) => track.id === selectedLibraryTrackId);
                  if (selected) queueTrackToDeck('B', selected);
                }}
              >
                Naar B
              </button>
            </div>

            <div className="dj-live-panel">
              <div className="dj-live-head-row">
                <div className="dj-live-head">Radiojar live</div>
                <button
                  className={`dj-gear-btn ${showRadiojarSettings ? 'active' : ''}`}
                  title="Radiojar instellingen"
                  onClick={() => setShowRadiojarSettings((state) => !state)}
                >
                  &#9881;
                </button>
              </div>
              <div className="dj-live-status">{radiojarStatus}</div>
              {radiojarError && <div className="dj-live-error">{radiojarError}</div>}
              {showRadiojarSettings && (
                <div className="dj-live-form">
                  <input value={radiojarConfig.host} onChange={(event) => setRadiojarConfig((state) => ({ ...state, host: event.target.value }))} placeholder="Host" />
                  <input value={radiojarConfig.port} onChange={(event) => setRadiojarConfig((state) => ({ ...state, port: event.target.value }))} placeholder="Poort" />
                  <input value={radiojarConfig.mountPoint} onChange={(event) => setRadiojarConfig((state) => ({ ...state, mountPoint: event.target.value }))} placeholder="Mount point" />
                  <input value={radiojarConfig.username} onChange={(event) => setRadiojarConfig((state) => ({ ...state, username: event.target.value }))} placeholder="Username" />
                  <input type="password" value={radiojarConfig.password} onChange={(event) => setRadiojarConfig((state) => ({ ...state, password: event.target.value }))} placeholder="Password" />
                </div>
              )}
              <div className="dj-live-actions">
                <button className="go" onClick={connectRadiojar} disabled={radiojarLoading}>{radiojarLoading ? 'Bezig...' : 'Ga live'}</button>
                <button className="stop" onClick={stopRadiojar} disabled={radiojarLoading}>Stop stream</button>
              </div>
            </div>
          </div>

          {renderDeck('B', deckB)}
        </div>

        <section className="dj-library">
          <div className="dj-library-head">
            <h2>Bibliotheek</h2>
            <button onClick={() => uploadLibraryRef.current?.click()}>Bestanden toevoegen</button>
            <input
              type="file"
              hidden
              multiple
              accept="audio/*"
              ref={uploadLibraryRef}
              onChange={(event) => {
                addFilesToLibrary(event.target.files);
                event.target.value = '';
              }}
            />
          </div>

          <div className="dj-library-tools-grid">
            <div className="dj-library-box">
              <h3>Server-afspeellijsten</h3>
              <p className="dj-library-small">
                Sla de huidige bibliotheek op met een naam, laad deze later opnieuw en werk bestaande afspeellijsten bij.
              </p>
              <div className="dj-playlist-form">
                <select
                  value={selectedSavedPlaylistId ?? ''}
                  onChange={(event) => {
                    const raw = event.target.value;
                    setSelectedSavedPlaylistId(raw ? Number(raw) : null);
                  }}
                >
                  <option value="">Selecteer afspeellijst</option>
                  {savedPlaylists.map((item) => (
                    <option key={item.id} value={item.id}>{item.name} ({item.songCount})</option>
                  ))}
                </select>
                <input
                  value={savedPlaylistName}
                  onChange={(event) => setSavedPlaylistName(event.target.value)}
                  placeholder="Naam afspeellijst"
                />
                <div className="dj-folder-actions">
                  <button onClick={() => { void createSavedPlaylist(); }} disabled={playlistBusy}>Opslaan nieuw</button>
                  <button onClick={() => { void createSavedPlaylist(buildAutoPlaylistName()); }} disabled={playlistBusy}>Snel opslaan</button>
                  <button onClick={updateSavedPlaylistById} disabled={playlistBusy || !selectedSavedPlaylistId}>Bijwerken</button>
                  <button onClick={loadSavedPlaylistById} disabled={playlistBusy || !selectedSavedPlaylistId}>Laden</button>
                </div>
              </div>

              <p className="dj-library-small" style={{ marginTop: 10 }}>
                Kies ook direct uit afspeellijsten die je in Spotify Dashboard hebt opgebouwd (events/rondes).
              </p>
              <div className="dj-playlist-form">
                <select
                  value={selectedDashboardPlaylistId}
                  onChange={(event) => setSelectedDashboardPlaylistId(event.target.value)}
                >
                  <option value="">Selecteer dashboard-afspeellijst</option>
                  {dashboardPlaylists.map((item) => (
                    <option key={item.id} value={item.id}>{item.name} ({item.songCount})</option>
                  ))}
                </select>
                <div className="dj-folder-actions">
                  <button onClick={loadDashboardPlaylistById} disabled={!selectedDashboardPlaylistId}>Laden uit dashboard</button>
                </div>
              </div>
            </div>
          </div>

          {libraryStatus && <div className="dj-status-line">{libraryStatus}</div>}
          {playlistStatus && <div className="dj-status-line">{playlistStatus}</div>}

          <p className="dj-library-note">
            Kies links een beschikbare map. De inhoud verschijnt in het midden. Voeg daar nummers toe aan je werk-afspeellijst rechts en sla die vervolgens op.
          </p>

          <div className="dj-library-columns">
            <div className="dj-library-col">
              <div className="dj-col-head">
                <h3>Beschikbare mappen</h3>
                <span>{musicFolders.length}</span>
              </div>
              <ul className="dj-folder-list dj-folder-browser-list">
                {musicFolders.length === 0 && (
                  <li className="dj-library-empty">Geen mappen gevonden.</li>
                )}
                {musicFolders.map((folder) => (
                  <li
                    key={folder.id}
                    className={selectedMusicFolderId === folder.id ? 'selected' : ''}
                    onClick={() => setSelectedMusicFolderId(folder.id)}
                  >
                    <div>
                      <strong>{folder.label || folder.path}</strong>
                      <span>{folder.path}</span>
                    </div>
                    {!folder.readOnly && <button onClick={(event) => { event.stopPropagation(); void removeMusicFolder(folder.id); }} disabled={libraryBusy}>Verwijder</button>}
                  </li>
                ))}
              </ul>
            </div>

            <div className="dj-library-col">
              <div className="dj-col-head">
                <h3>Mapinhoud</h3>
                <span>{folderLibraryTracks.length} tracks</span>
              </div>
              <ul className="dj-library-list">
                {!selectedMusicFolderId && <li className="dj-library-empty">Selecteer links een map</li>}
                {selectedMusicFolderId && folderLibraryTracks.length === 0 && <li className="dj-library-empty">Geen tracks gevonden in deze map</li>}
                {folderLibraryTracks.map((track) => (
                  <li
                    key={track.id}
                    className={selectedLibraryTrackId === track.id ? 'selected' : ''}
                    onClick={() => setSelectedLibraryTrackId(track.id)}
                  >
                    <div>
                      <strong>{track.title}</strong>
                      <span>{track.artist}</span>
                      {!hasStableAudioUrl(track.audioUrl) && <span>Zonder audio-link</span>}
                    </div>
                    <div className="dj-library-actions">
                      <button onClick={(event) => { event.stopPropagation(); addTrackToWorkingPlaylist(track); }}>+ Playlist</button>
                      <button disabled={!hasStableAudioUrl(track.audioUrl)} onClick={(event) => { event.stopPropagation(); queueTrackToDeck('A', track); }}>A</button>
                      <button disabled={!hasStableAudioUrl(track.audioUrl)} onClick={(event) => { event.stopPropagation(); queueTrackToDeck('B', track); }}>B</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="dj-library-col playlist-column">
              <div className="dj-col-head">
                <h3>Werk-afspeellijst</h3>
                <span>{filteredPlaylistTracks.length}/{selectedPlaylistTracks.length}</span>
              </div>

              <div className="dj-playlist-track-tools">
                <input
                  value={playlistTrackSearch}
                  onChange={(event) => setPlaylistTrackSearch(event.target.value)}
                  placeholder="Zoek op titel of artiest"
                />
                <select
                  value={playlistTrackSourceFilter}
                  onChange={(event) => setPlaylistTrackSourceFilter(event.target.value as 'all' | DjTrack['source'])}
                >
                  <option value="all">Alle bronnen</option>
                  <option value="playlist">Prepare playlist</option>
                  <option value="server">Server map</option>
                  <option value="upload">Lokale upload</option>
                  <option value="saved">Opgeslagen</option>
                </select>
              </div>

              <div className="dj-playlist-bulk-actions">
                <button onClick={() => queueFilteredPlaylistTracks('A')} disabled={filteredPlaylistTracks.length === 0}>Alles naar A</button>
                <button onClick={() => queueFilteredPlaylistTracks('B')} disabled={filteredPlaylistTracks.length === 0}>Alles naar B</button>
                <button onClick={() => queueFilteredPlaylistTracks('split')} disabled={filteredPlaylistTracks.length === 0}>Om en om A/B</button>
                <button onClick={() => { void repairSelectedPlaylistAudioLinks(); }} disabled={playlistBusy || selectedPlaylistTracks.length === 0}>Audio-links herstellen</button>
              </div>

              <ul className="dj-playlist-track-list">
                {selectedPlaylistTracks.length === 0 && <li className="dj-library-empty">Nog geen nummers in de werk-afspeellijst</li>}
                {selectedPlaylistTracks.length > 0 && filteredPlaylistTracks.length === 0 && <li className="dj-library-empty">Geen nummers gevonden voor dit filter</li>}
                {filteredPlaylistTracks.map((track) => (
                  <li key={track.id}>
                    <div>
                      <strong>{track.title}</strong>
                      <span>{track.artist}</span>
                      {!hasStableAudioUrl(track.audioUrl) && <span>Zonder audio-link</span>}
                    </div>
                    <div className="dj-library-actions">
                      <button onClick={() => removeTrackFromWorkingPlaylist(track.id)}>Verwijder</button>
                      <button disabled={!hasStableAudioUrl(track.audioUrl)} onClick={() => queueTrackToDeck('A', track)}>A</button>
                      <button disabled={!hasStableAudioUrl(track.audioUrl)} onClick={() => queueTrackToDeck('B', track)}>B</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DjDeck;